// contractAuthService.js
import { readContract, writeContract, waitForTransactionReceipt } from '@wagmi/core';
import { abi } from './auth_abi';
import addresses from './addresses';
import utils from '@/composables/utils';
import { config } from './walletConfig';

const { convertToWei, convertFromWei } = utils;
const DEFAULT_DECIMALS = 18;

/**
 * 监控交易状态并返回 receipt
 * @param {string} txHash 交易哈希
 * @returns {Promise<any>}
 */
export async function monitorTransaction(txHash) {
  try {
    const receipt = await waitForTransactionReceipt(config, { hash: txHash });
    return receipt;
  } catch (error) {
    console.error("Error in monitorTransaction:", error);
    throw error;
  }
}

/**
 * 获取合约的 decimals
 * @returns {Promise<number>}
 */
export async function getDecimals() {
  try {
    const decimals = await readContract(config, {
      abi: abi,
      address: addresses.authContractAddress,
      functionName: "decimals",
      args: []
    });
    return Number(decimals);
  } catch (error) {
    console.error("Error in getDecimals:", error);
    throw error;
  }
}

/**
 * 获取总供给量
 * @returns {Promise<string>}
 */
export async function totalSupply() {
  try {
    const decimals = await getDecimals();
    const supply = await readContract(config, {
      abi: abi,
      address: addresses.authContractAddress,
      functionName: "totalSupply",
      args: []
    });
    return convertFromWei(supply, decimals, 2);
  } catch (error) {
    console.error("Error in totalSupply:", error);
    throw error;
  }
}

/**
 * 执行 ERC20 approve 操作
 * @param {string} spender 要授权的地址
 * @param {number|string} amount 授权数量（常规单位）
 * @param {number} decimals 小数位，默认为18
 * @returns {Promise<any>}
 */
export async function approve(spender, amount, decimals = DEFAULT_DECIMALS) {
  try {
    const amountWei = convertToWei(amount, decimals);
    const tx = await writeContract(config, {
      chainId: 137,
      abi: abi,
      address: addresses.authContractAddress,
      functionName: "approve",
      args: [spender, amountWei]
    });
    await monitorTransaction(tx);
    return tx;
  } catch (error) {
    console.error("Error in approve:", error);
    throw error;
  }
}

/**
 * 执行 ERC20 transfer 操作
 * @param {string} to 接收地址
 * @param {number|string} amount 转账数量（常规单位）
 * @param {number} decimals 小数位，默认为18
 * @returns {Promise<any>}
 */
export async function transfer(to, amount, decimals = DEFAULT_DECIMALS) {
  try {
    const amountWei = convertToWei(amount, decimals);
    const tx = await writeContract(config, {
      chainId: 137,
      abi: abi,
      address: addresses.authContractAddress,
      functionName: "transfer",
      args: [to, amountWei]
    });
    await monitorTransaction(tx);
    return tx;
  } catch (error) {
    console.error("Error in transfer:", error);
    throw error;
  }
}

/**
 * 执行 ERC20 transferFrom 操作
 * @param {string} from 转出地址
 * @param {string} to 接收地址
 * @param {number|string} amount 转账数量（常规单位）
 * @param {number} decimals 小数位，默认为18
 * @returns {Promise<any>}
 */
export async function transferFrom(from, to, amount, decimals = DEFAULT_DECIMALS) {
  try {
    const amountWei = convertToWei(amount, decimals);
    const tx = await writeContract(config, {
      chainId: 137,
      abi: abi,
      address: addresses.authContractAddress,
      functionName: "transferFrom",
      args: [from, to, amountWei]
    });
    await monitorTransaction(tx);
    return tx;
  } catch (error) {
    console.error("Error in transferFrom:", error);
    throw error;
  }
}

/**
 * 获取合约的授权额度
 * @param {string} owner 授权者地址
 * @param {string} spender 被授权者地址
 * @returns {Promise<number>}
 */
export async function allowance(owner, spender) {
  try {
    const allowance = await readContract(config, {
      abi: abi,
      address: addresses.authContractAddress,
      functionName: "allowance",
      args: [owner, spender]
    });
    return convertFromWei(allowance, DEFAULT_DECIMALS, 2);
  } catch (error) {
    console.error("Error in allowance:", error);
    throw error;
  }
}

/**
 * 查询账户余额
 * @param {string} account 要查询的账户地址
 * @returns {Promise<string>} 返回格式化后的余额
 */
export async function balanceOf(account) {
  try {
    const balance = await readContract(config, {
      abi: abi,
      address: addresses.authContractAddress,
      functionName: "balanceOf",
      args: [account]
    });
    return convertFromWei(balance, DEFAULT_DECIMALS, 2);
  } catch (error) {
    console.error("查询余额时发生错误:", error);
    throw error;
  }
}

/**
 * 增加授权额度
 * @param {string} spender 被授权地址
 * @param {number|string} addedValue 增加的额度
 * @returns {Promise<any>}
 */
export async function increaseAllowance(spender, addedValue) {
  try {
    const amountWei = convertToWei(addedValue, DEFAULT_DECIMALS);
    const tx = await writeContract(config, {
      chainId: 137,
      abi: abi,
      address: addresses.authContractAddress,
      functionName: "increaseAllowance",
      args: [spender, amountWei]
    });
    await monitorTransaction(tx);
    return tx;
  } catch (error) {
    console.error("Error in increaseAllowance:", error);
    throw error;
  }
}

/**
 * 减少授权额度
 * @param {string} spender 被授权地址
 * @param {number|string} subtractedValue 减少的额度
 * @returns {Promise<any>}
 */
export async function decreaseAllowance(spender, subtractedValue) {
  try {
    const amountWei = convertToWei(subtractedValue, DEFAULT_DECIMALS);
    const tx = await writeContract(config, {
      chainId: 137,
      abi: abi,
      address: addresses.authContractAddress,
      functionName: "decreaseAllowance",
      args: [spender, amountWei]
    });
    await monitorTransaction(tx);
    return tx;
  } catch (error) {
    console.error("Error in decreaseAllowance:", error);
    throw error;
  }
}

/**
 * 获取合约所有者地址
 * @returns {Promise<string>}
 */
export async function owner() {
  try {
    const ownerAddress = await readContract(config, {
      abi: abi,
      address: addresses.authContractAddress,
      functionName: "owner",
      args: []
    });
    return ownerAddress;
  } catch (error) {
    console.error("Error in owner:", error);
    throw error;
  }
}

/**
 * 转移合约所有权
 * @param {string} newOwner 新的所有者地址
 * @returns {Promise<any>}
 */
export async function transferOwnership(newOwner) {
  try {
    const tx = await writeContract(config, {
      chainId: 137,
      abi: abi,
      address: addresses.authContractAddress,
      functionName: "transferOwnership",
      args: [newOwner]
    });
    await monitorTransaction(tx);
    return tx;
  } catch (error) {
    console.error("Error in transferOwnership:", error);
    throw error;
  }
}

/**
 * 暂停合约
 * @returns {Promise<any>}
 */
export async function pause() {
  try {
    const tx = await writeContract(config, {
      chainId: 137,
      abi: abi,
      address: addresses.authContractAddress,
      functionName: "pause",
      args: []
    });
    await monitorTransaction(tx);
    return tx;
  } catch (error) {
    console.error("Error in pause:", error);
    throw error;
  }
}

/**
 * 恢复合约
 * @returns {Promise<any>}
 */
export async function unpause() {
  try {
    const tx = await writeContract(config, {
      chainId: 137,
      abi: abi,
      address: addresses.authContractAddress,
      functionName: "unpause",
      args: []
    });
    await monitorTransaction(tx);
    return tx;
  } catch (error) {
    console.error("Error in unpause:", error);
    throw error;
  }
}

/**
 * 获取合约名称
 * @returns {Promise<string>}
 */
export async function name() {
  try {
    const tokenName = await readContract(config, {
      abi: abi,
      address: addresses.authContractAddress,
      functionName: "name",
      args: []
    });
    return tokenName;
  } catch (error) {
    console.error("Error in name:", error);
    throw error;
  }
}

/**
 * 获取合约符号
 * @returns {Promise<string>}
 */
export async function symbol() {
  try {
    const tokenSymbol = await readContract(config, {
      abi: abi,
      address: addresses.authContractAddress,
      functionName: "symbol",
      args: []
    });
    return tokenSymbol;
  } catch (error) {
    console.error("Error in symbol:", error);
    throw error;
  }
}

/**
 * 添加新角色
 * @param {string} roleName 角色名称
 * @returns {Promise<any>}
 */
export async function addRole(roleName) {
  try {
    const tx = await writeContract(config, {
      chainId: 137,
      abi: abi,
      address: addresses.authContractAddress,
      functionName: "addRole",
      args: [roleName]
    });
    await monitorTransaction(tx);
    return tx;
  } catch (error) {
    console.error("Error in addRole:", error);
    throw error;
  }
}

/**
 * 授权地址
 * @param {number} roleId 角色ID
 * @param {string} addr 要授权的地址
 * @param {string} description 描述
 * @returns {Promise<any>}
 */
export async function authorize(roleId, addr, description) {
  try {
    const tx = await writeContract(config, {
      chainId: 137,
      abi: abi,
      address: addresses.authContractAddress,
      functionName: "authorize",
      args: [roleId, addr, description]
    });
    await monitorTransaction(tx);
    return tx;
  } catch (error) {
    console.error("Error in authorize:", error);
    throw error;
  }
}

/**
 * 取消授权地址
 * @param {number} roleId 角色ID
 * @param {string} addr 要取消授权的地址
 * @returns {Promise<any>}
 */
export async function deauthorize(roleId, addr) {
  try {
    const tx = await writeContract(config, {
      chainId: 137,
      abi: abi,
      address: addresses.authContractAddress,
      functionName: "deauthorize",
      args: [roleId, addr]
    });
    await monitorTransaction(tx);
    return tx;
  } catch (error) {
    console.error("Error in deauthorize:", error);
    throw error;
  }
}

/**
 * 取消所有授权
 * @returns {Promise<any>}
 */
export async function deauthorizeAll() {
  try {
    const tx = await writeContract(config, {
      chainId: 137,
      abi: abi,
      address: addresses.authContractAddress,
      functionName: "deauthorizeAll",
      args: []
    });
    await monitorTransaction(tx);
    return tx;
  } catch (error) {
    console.error("Error in deauthorizeAll:", error);
    throw error;
  }
}

/**
 * 获取所有授权地址信息
 * @returns {Promise<Array>} 返回授权地址信息数组
 */
export async function getAuthorizedAddressesInfo() {
  try {
    const addressesInfo = await readContract(config, {
      abi: abi,
      address: addresses.authContractAddress,
      functionName: "getAuthorizedAddressesInfo",
      args: []
    });
    return addressesInfo;
  } catch (error) {
    console.error("Error in getAuthorizedAddressesInfo:", error);
    throw error;
  }
}

/**
 * 获取角色信息
 * @param {number} roleId 角色ID
 * @returns {Promise<Object>} 返回角色信息
 */
export async function getRole(roleId) {
  try {
    const role = await readContract(config, {
      abi: abi,
      address: addresses.authContractAddress,
      functionName: "getRole",
      args: [roleId]
    });
    return role;
  } catch (error) {
    console.error("Error in getRole:", error);
    throw error;
  }
}

/**
 * 获取所有角色信息
 * @returns {Promise<Array>} 返回角色信息数组
 */
export async function getRolesInfo() {
  try {
    const roles = await readContract(config, {
      abi: abi,
      address: addresses.authContractAddress,
      functionName: "getRolesInfo",
      args: []
    });
    return roles;
  } catch (error) {
    console.error("Error in getRolesInfo:", error);
    throw error;
  }
}

/**
 * 检查地址是否被授权
 * @param {number} roleId 角色ID
 * @param {string} addr 要检查的地址
 * @returns {Promise<boolean>} 返回是否授权
 */
export async function isAddressAuthorized(roleId, addr) {
  try {
    const isAuthorized = await readContract(config, {
      abi: abi,
      address: addresses.authContractAddress,
      functionName: "isAddressAuthorized",
      args: [roleId, addr]
    });
    return isAuthorized;
  } catch (error) {
    console.error("Error in isAddressAuthorized:", error);
    throw error;
  }
}

/**
 * 放弃所有权
 * @returns {Promise<any>}
 */
export async function renounceOwnership() {
  try {
    const tx = await writeContract(config, {
      chainId: 137,
      abi: abi,
      address: addresses.authContractAddress,
      functionName: "renounceOwnership",
      args: []
    });
    await monitorTransaction(tx);
    return tx;
  } catch (error) {
    console.error("Error in renounceOwnership:", error);
    throw error;
  }
}
